import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SoarQueryKeys } from '../queryKeys';
import { ActionCatalogDto, catalogService, TriggerEvent } from '../services/catalogService';

export const useCatalog = () => {
  const {
    data: entities = [],
    isLoading: isLoadingEntities,
    isFetched: isFetchedEntities,
  } = useQuery({
    queryKey: [SoarQueryKeys.CATALOG_ENTITIES],
    queryFn: async () => {
      const data = await catalogService.getEntities();
      return Array.isArray(data) ? data : [];
    },
  });

  const { data: events = [], isLoading: isLoadingEvents } = useQuery({
    queryKey: [SoarQueryKeys.CATALOG_EVENTS],
    queryFn: async () => {
      if (entities.length === 0) return [];
      const eventResults = await Promise.all(entities.map(entity => catalogService.getEvents(entity.entityType)));
      return eventResults.flat().reduce((acc, newEvent) => {
        if (!acc.some(e => e.eventId === newEvent.eventId)) {
          return [...acc, newEvent];
        }
        return acc;
      }, [] as TriggerEvent[]);
    },
    enabled: entities.length > 0,
  });

  const { data: actions = [], isLoading: isLoadingActions } = useQuery({
    queryKey: [SoarQueryKeys.CATALOG_ACTIONS],
    queryFn: async () => {
      if (entities.length === 0) return [];
      const actionResults = await Promise.all(entities.map(entity => catalogService.getActions(entity.entityType)));
      return actionResults.flat().reduce((acc, newAction) => {
        if (!acc.some(a => a.actionId === newAction.actionId)) {
          return [...acc, newAction];
        }
        return acc;
      }, [] as ActionCatalogDto[]);
    },
    enabled: entities.length > 0,
  });

  const isLoading = isLoadingEntities || isLoadingEvents || isLoadingActions;
  const isFetched = isFetchedEntities;

  const getEntityName = useCallback(
    (type: string): string => {
      const entity = entities.find(e => e.entityType === type);
      return entity?.entityName || type;
    },
    [entities]
  );

  const getEventName = useCallback(
    (id: string): string => {
      const event = events.find(e => e.eventId === id);
      return event?.displayName || event?.eventName || id;
    },
    [events]
  );

  return {
    entities,
    events,
    actions,
    isLoading,
    isFetched,
    fetchCatalog: () => {}, // No-op, useQuery handles it
    getEntityName,
    getEventName,
  };
};
